import { AuthService } from '@services/auth.service';
import { Component, OnInit, Input, ChangeDetectorRef } from '@angular/core';
import { FormArray, UntypedFormBuilder, Validators } from '@angular/forms';

import { FormEditBaseComponent } from '@components/form-edit-base.component';
import { User } from '@classes/user/user';
import { Role } from '@classes/role';
import { UiService } from '@services/ui.service';
import { UsersService } from '@services/users.service';
import { UserDetail } from '@classes/user/user-detail';
import { Client } from '@classes/client';
import { RoleName } from '@enums/role-name.enum';
import { RolesService } from '@services/role.service';
import { SitesService } from '@services/sites.service';
import { ClientsService } from '@services/clients.service';
import { Site } from '@classes/site';

@Component({
  selector: 'app-user-edit',
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.scss'],
})
export class UserEditComponent extends FormEditBaseComponent implements OnInit {
  @Input() user: User;

  rolesList: Role[];
  clientsList: Client[] = [];

  selectableClients: Client[] = [];

  sitesList: Site[] = [];
  selectableSites: Site[] = [];
  selRole: Role;
  userNamePattern = '^[a-z0-9._]{3,50}$';
  canChooseClient: boolean;
  canChooseSite: boolean;
  // canChooseFeatures: boolean = false;
  // canHaveFeatures: boolean = true;

  stringMaxLength = 50;

  subcontractorValidationError: string | null = null;

  // allFeatures: Feature[] = [];
  // featuresFormArray: FormArray;

  constructor(
    protected uiSrv: UiService,
    protected fb: UntypedFormBuilder,
    private rolesSrv: RolesService,
    private usersSrv: UsersService,
    private clientsSrv: ClientsService,
    private sitesSrv: SitesService,
    private authSrv: AuthService,
    private changeDetectorRef: ChangeDetectorRef,
    // private featuresSrv: FeaturesService
  ) {
    super(uiSrv, fb);
  }

  ngOnInit() {
    super.ngOnInit();

    const userRole = this.authSrv.getUser().role.name;
    const CanChooseClientAllowedRoles = [
      RoleName.bizzdev,
      RoleName.super_admin,
      RoleName.admin_client,
      RoleName.admin,
      RoleName.admin_subcontractor,
    ];
    this.canChooseClient = CanChooseClientAllowedRoles.includes(userRole);

    // this.canChooseFeatures = this.authSrv.getUser().role.name === RoleName.bizzdev || this.authSrv.getUser().role.name === RoleName.super_admin || this.authSrv.getUser().role.name === RoleName.admin;

    // if (this.user) {
    //   this.canHaveFeatures = this.user.role.name !== RoleName.bizzdev && this.user.role.name !== RoleName.super_admin;
    // }

    this.initSubscriptions();
  }

  get isClientRequired(): boolean {
    return (
      this.selRole &&
      this.selRole.name !== RoleName.admin &&
      this.selRole.name !== RoleName.admin_subcontractor
    );
  }

  get isSiteRequired(): boolean {
    // return this.selRole && this.selRole.name !== RoleName.admin;
    return true;
  }

  get canAddClient(): boolean {
    const restrictedRoles = [
      RoleName.admin,
      RoleName.admin_subcontractor,
      RoleName.client,
      RoleName.admin_client,
      RoleName.driver,
      RoleName.dispatcher,
    ];
    return (
      !this.selRole || !restrictedRoles.some((r) => r === this.selRole.name)
    );
  }

  get canAddSite(): boolean {
    const restrictedRoles = [];
    return (
      !this.selRole || !restrictedRoles.some((r) => r === this.selRole.name)
    );
  }

  onAddClient(client?: Client): void {
    if (!client) {
      this.generateSelectableClients();
    }

    const control = this.fb.control(client || null);
    control.setValidators(Validators.required);
    this.getArray('clients').push(control);

    this.changeDetectorRef.detectChanges();
  }

  onAddSite(site?: Site): void {
    if (!site) {
      this.generateSelectableSites();
    }
    const control = this.fb.control(site || null);
    if (this.isSiteRequired) {
      control.setValidators(Validators.required);
    }
    this.getArray('sites').push(control);

    this.changeDetectorRef.detectChanges();
  }

  onRemoveClient(index: number): void {
    this.getArray('clients').removeAt(index);

    this.generateSelectableClients();
  }

  onRemoveSite(index: number): void {
    this.getArray('sites').removeAt(index);
    this.generateSelectableSites();
  }

  onSubmit(): void {
    // Réinitialiser le message d'erreur
    this.subcontractorValidationError = null;

    // Valider la règle admin_subcontractor
    if (!this.validateSubcontractorRole()) {
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    if (this.user) {
      this.update();
    } else {
      this.create();
    }
  }

  onCancel(): void {
    this.editFinished.emit();
  }

  private validateSubcontractorRole(): boolean {
    // Vérifier si le rôle sélectionné est admin_subcontractor
    if (this.selRole && this.selRole.name === RoleName.admin_subcontractor) {
      // Si allSites est coché, vérifier que tous les clients sont des sous-traitants
      if (this.editForm.value.allSites) {
        const formClients: Client[] = this.editForm.value.clients.filter(
          (c: any) => c != null,
        );

        // Récupérer les clients complets depuis clientsList
        const clientIds = formClients.map((c) => c.id);
        const fullClients = this.clientsList.filter((client) =>
          clientIds.includes(client.id),
        );

        const nonSubcontractorClients = fullClients.filter(
          (client) => !client.isSubcontractor,
        );

        if (nonSubcontractorClients.length > 0) {
          const clientNames = nonSubcontractorClients
            .map((client) => client.name)
            .join(', ');
          this.subcontractorValidationError = `Le rôle "Administrateur sous-traitant" ne peut être attribué qu'à des utilisateurs d'agences sous-traitantes. Les agences suivantes ne sont pas des agences sous-traitantes : ${clientNames}`;
          return false;
        }
      } else {
        // Sinon, vérifier les sites sélectionnés
        const sites: Site[] = this.editForm.value.sites.filter(
          (s: any) => s != null,
        );

        // Récupérer les IDs des clients des sites et les chercher dans clientsList
        const clientIds = sites
          .flatMap((site) => site.clients?.map((c) => c.id) || [])
          .filter((id) => id != null);
        const fullClients = this.clientsList.filter((client) =>
          clientIds.includes(client.id),
        );

        // Vérifier que toutes les agences sélectionnées sont des agences sous-traitantes
        const nonSubcontractorClients = fullClients.filter(
          (client) => !client.isSubcontractor,
        );

        if (nonSubcontractorClients.length > 0) {
          const clientNames = nonSubcontractorClients
            .map((client) => client.name)
            .join(', ');
          this.subcontractorValidationError = `Le rôle "Administrateur sous-traitant" ne peut être attribué qu'à des utilisateurs d'agences sous-traitantes. Les donneurs d'ordre suivants ne sont pas rattachés à des agences sous-traitantes : ${clientNames}`;
          return false;
        }
      }
    }

    return true;
  }

  private update(): void {
    const userCopy: User = JSON.parse(JSON.stringify(this.user));
    userCopy.username = this.editForm.value.username;
    userCopy.email = this.editForm.value.email;
    userCopy.role = this.selRole;
    if (userCopy.detail == null) {
      userCopy.detail = new UserDetail();
    }

    userCopy.sites = this.editForm.value.sites;
    userCopy.allSites =
      this.selRole.name === 'admin_client' || this.selRole.name === 'client'
        ? false
        : this.editForm.value.allSites;
    userCopy.detail.firstname = this.editForm.value.detail.firstname;
    userCopy.detail.lastname = this.editForm.value.detail.lastname;
    userCopy.detail.phone = this.editForm.value.detail.phone;

    userCopy.clients = this.editForm.value.clients.filter((c) => c != null);

    // const selectedFeaturesId = this.editForm.value.features
    //   .map((checked, i) => checked ? this.allFeatures[i].id : null)
    //   .filter(v => v !== null);

    this.usersSrv.put(userCopy).subscribe((userResponse) => {
      // if (selectedFeaturesId.length > 0 && !this.editForm.get('features').pristine && this.canHaveFeatures) {
      //   this.featuresSrv.putUserFeatures(userResponse.id, selectedFeaturesId).subscribe((user) => {
      //     this.usersSrv.updateUsersListSubject(user);
      //     this.isLoading = false;
      //   })
      // } else {
      this.isLoading = false;
      // }
    });
  }

  private create(): void {
    this.user = new User(
      this.editForm.value.username,
      this.editForm.value.email,
      this.selRole,
      this.editForm.value.clients.filter((c) => c != null),
      this.editForm.value.sites.filter((s) => s != null),
      this.editForm.value.allSites,
      this.editForm.value.detail.firstname,
      this.editForm.value.detail.lastname,
      this.editForm.value.detail.phone,
    );

    // const selectedFeaturesId = this.editForm.value.features
    //   .map((checked, i) => checked ? this.allFeatures[i].id : null)
    //   .filter(v => v !== null);

    this.usersSrv.post(this.user).subscribe(
      (userResponse) => {
        // if (selectedFeaturesId.length > 0 && this.canHaveFeatures) {
        //   this.featuresSrv.putUserFeatures(userResponse.id, selectedFeaturesId).subscribe((user) => {
        //     this.usersSrv.updateUsersListSubject(user);
        //     this.isLoading = false;
        //   })
        // } else {
        this.isLoading = false;
        // }
      },
      () => (this.user = null),
    );
  }

  private initSubscriptions(): void {
    this.isLoading = true;

    const rolesPromise = new Promise((resolve, reject) => {
      this.rolesSrv.getList().subscribe((rolesFromSrv) => {
        this.rolesList = rolesFromSrv;
        resolve(rolesFromSrv);
      });
    });

    const clientsPromise = new Promise((resolve, reject) => {
      this.clientsSrv.getList().subscribe((clientsFromSrv) => {
        this.clientsList = clientsFromSrv;
        resolve(clientsFromSrv);
      });
    });

    // const featuresPromise = new Promise((resolve, reject) => {
    //   if (this.user) {
    //     this.featuresSrv.getByClient(this.user.clients[0].id).subscribe(featuresFromSrv => {
    //       this.allFeatures = featuresFromSrv;
    //       resolve(featuresFromSrv);
    //     })
    //   } else {
    //     if (this.canChooseClient) {
    //       resolve('ok');
    //     } else {
    //       this.featuresSrv.get().subscribe(featuresFromSrv => {
    //         this.allFeatures = featuresFromSrv;
    //         resolve(featuresFromSrv);
    //       })
    //     }
    //   }
    // })

    Promise.all([rolesPromise, clientsPromise]).then(() => {
      this.initForm();
      this.isLoading = false;
    });
  }

  private initForm(): void {
    let username = '';
    let email = '';
    let role = '';
    let firstname = '';
    let lastname = '';
    let phone = '';
    let clients: Client[] = [];
    let sites: Site[] = [];
    let allSites: boolean = false;
    // let features = []

    if (this.user) {
      username = this.user.username;
      email = this.user.email;
      role = this.user.role.id;
      clients = this.user.clients;
      sites = this.user.sites;
      allSites = this.user.allSites;
      this.selRole = this.user.role;
      // features = this.user.features;

      if (this.user.detail) {
        firstname = this.user.detail.firstname;
        lastname = this.user.detail.lastname;
        phone = this.user.detail.phone;
      }
    }
    // else {
    //   // si l'utilisateur n'est pas superadmin/bizzdev alors il doit rajouter des users dans son client
    //   // if (!this.canChooseClient) {
    //   //   clients[0] = this.clientsList[0];
    //   // }
    // }

    this.editForm = this.fb.group({
      username: [
        username,
        [
          Validators.required,
          Validators.pattern(this.userNamePattern),
          Validators.maxLength(this.stringMaxLength),
        ],
      ],
      email: [
        email,
        [Validators.required, Validators.email, Validators.maxLength(100)],
      ],
      role: [role, Validators.required],
      clients: this.fb.array([]),
      sites: this.fb.array([]),
      allSites: [allSites],
      detail: this.fb.group({
        firstname: [firstname, Validators.maxLength(this.stringMaxLength)],
        lastname: [lastname, Validators.maxLength(this.stringMaxLength)],
        phone: [phone, Validators.maxLength(this.stringMaxLength)],
      }),
      // features: this.fb.array([])
    });

    this.canChooseSite = false;

    if (clients && clients.length) {
      clients.map((c) => this.onAddClient(c));
    } else {
      this.onAddClient();
    }

    if (this.user && this.user.clients) {
      let clientIdsUser = [];
      this.user.clients.map((oneClient) => {
        clientIdsUser.push(oneClient.id);
      });
      this.sitesSrv
        .getListByClients(clientIdsUser)
        .subscribe((sitesFromSrv) => {
          this.sitesList = sitesFromSrv;
          this.changeDetectorRef.detectChanges();
          this.generateSelectableSites();

          this.canChooseSite = true;
        });
    }

    if (sites && sites.length > 0) {
      sites.map((s) => this.onAddSite(s));
    } else if (!this.user?.allSites) {
      // this.onAddSite();
    }

    this.getArray('clients').valueChanges.subscribe((results) => {
      const clientIds = [];
      let canRequest = true;
      if (results[0] !== null && results[0] !== '') {
        results.map((oneClient) => {
          if (oneClient && oneClient.id) {
            clientIds.push(oneClient.id);
          } else {
            canRequest = false;
          }
        });

        if (clientIds[0] && canRequest) {
          this.sitesList = [];
          if (this.getArray('sites')) {
            this.getArray('sites').value.map((siteControl, index) => {
              this.onRemoveSite(index);
            });
          }

          this.sitesSrv
            .getListByClients(clientIds)
            .subscribe((sitesFromSrv) => {
              this.sitesList = sitesFromSrv;
              this.generateSelectableSites();
              this.changeDetectorRef.detectChanges();
              this.canChooseSite = true;
            });

          // this.featuresSrv.getByClient(clientIds[0]).subscribe(featuresFromSrv => {
          //   this.allFeatures = featuresFromSrv;

          //   this.addFeatures();
          // })
        }
      } else {
        this.canChooseSite = false;
      }
    });

    this.observeFormChanges();
    // this.addFeatures();

    // if (features?.length > 0) {
    //   this.allFeatures.map((feature, index) => {
    //     if (features.find(f => f.id === feature.id)) {
    //       let formArrayFeatures = this.editForm.get('features') as FormArray;
    //       formArrayFeatures.controls[index].setValue(true)
    //     }
    //   })
    // }
  }

  private observeFormChanges(): void {
    this.editForm.controls.role.valueChanges.subscribe((selId: string) => {
      this.selRole = this.rolesList.find((r) => r.id === selId);
      // Réinitialiser le message d'erreur de validation lorsque le rôle change
      this.subcontractorValidationError = null;

      if (this.selRole.name === 'driver') {
        this.editForm.controls.allSites.setValue(true);
      } else if (
        this.selRole.name === 'client' ||
        this.selRole.name === 'admin_client'
      ) {
        this.editForm.controls.allSites.setValue(false);
      }

      // this.canHaveFeatures = this.selRole.name !== 'bizzdev' && this.selRole.name !== 'super_admin';

      this.getArray('clients').controls.map((control) => {
        control.setValidators(Validators.required);

        control.updateValueAndValidity();
      });
    });

    this.editForm.controls.allSites.valueChanges.subscribe(
      (allSites: boolean) => {
        // Réinitialiser le message d'erreur de validation
        this.subcontractorValidationError = null;

        if (allSites) {
          this.editForm.controls.sites.clearValidators();
          this.editForm.controls.sites.updateValueAndValidity();
          this.getArray('sites').clear();
        } else {
          this.editForm.controls.sites.addValidators(Validators.required);
          this.getArray('sites').clear();
          this.onAddSite();
        }

        this.generateSelectableSites();
      },
    );

    // Réinitialiser le message d'erreur lorsque les sites changent
    this.editForm.controls.sites.valueChanges.subscribe(() => {
      this.subcontractorValidationError = null;
    });

    // Réinitialiser le message d'erreur lorsque les clients changent
    this.editForm.controls.clients.valueChanges.subscribe(() => {
      this.subcontractorValidationError = null;
    });
  }

  // addFeatures(): void {
  //   this.featuresFormArray = this.editForm.get('features') as FormArray;

  //   this.featuresFormArray.clear();

  //   this.allFeatures.forEach(() => this.featuresFormArray.push(new FormControl(false)));
  // }

  endForm(response: boolean) {
    if (response) {
      this.onSubmit();
    } else {
      this.onCancel();
    }
  }

  generateSelectableClients(): void {
    this.selectableClients = [...this.clientsList].filter(
      (client) =>
        !this.alreadyUsedClients.map((item) => item.id).includes(client.id),
    );
  }

  get clientsFormArray(): FormArray {
    return this.editForm.get('clients') as FormArray;
  }

  get alreadyUsedClients(): Client[] {
    return this.clientsFormArray.controls
      .map((control) => control.value)
      .filter((item) => item !== null);
  }

  generateSelectableSites(): void {
    this.selectableSites = [...this.sitesList].filter(
      (site) => !this.alreadyUsedSites.map((item) => item.id).includes(site.id),
    );
  }

  get sitesFormArray(): FormArray {
    return this.editForm.get('sites') as FormArray;
  }

  get alreadyUsedSites(): Site[] {
    return this.sitesFormArray.controls
      .map((control) => control.value)
      .filter((item) => item !== null);
  }
}
